import { AsyncPipe, Location, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyModule } from '@ngx-formly/core';
import {
  AuthActions,
  AuthInfo,
  Certificate,
  CourseContentActions,
  CoursePreviewCalculation,
  CourseSettingsService,
  ROUTE_HOME,
  selectAuthInfo,
  selectCourseContent,
  selectCoursesCertificate,
  selectPreviewCalculation,
} from '@shared';
import { NavigationComponent } from '@shared/components/navigation';
import { ProgressCircleComponent } from '@shared/components/progress';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { distinctUntilChanged } from 'rxjs';
import { AvatarComponent, RouterStoreService, User } from 'thkee-common';
@UntilDestroy()
@Component({
  selector: 'app-learninglab-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    NgStyle,
    AngularSvgIconModule,
    FormsModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    FormlyModule,
    ProgressCircleComponent,
    AvatarComponent,
    NavigationComponent,
    AsyncPipe,
    RouterLink,
  ],
})
export class LearninglabHeaderComponent implements OnInit {
  homeUrl: string = ROUTE_HOME;

  course$ = this.store.select(selectCourseContent);
  courseCertificate$ = this.store.select(selectCoursesCertificate);
  userAuthInfo$ = this.store.select(selectAuthInfo);
  userAuthInfo!: AuthInfo;

  certificateDetail!: Certificate;
  params: Params = {};
  user?: User;
  isPreviewCourse: boolean = false;
  queryParams: Params = {};

  title = inject(Title);
  pageTitle = $localize`Learning Lab`;

  previewData: CoursePreviewCalculation = {
    completed_subsections: 0,
    progress: 0,
    subsections_total: 0,
  };

  constructor(
    private store: Store,
    private routerStore: RouterStoreService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private courseSettingsService: CourseSettingsService
  ) {}

  ngOnInit(): void {
    this.routerStore
      .getParams()
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((params) => {
        this.params = params;
      });

    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
      this.queryParams = params;
      if (params['preview'] === 'true') {
        this.isPreviewCourse = true;
        this.getPreviewCount();
      } else {
        this.isPreviewCourse = false;
        this.getNotificationSettings(this.params['courseId']);
        this.getCertificateInfos();
      }
    });

    this.course$.pipe(untilDestroyed(this)).subscribe((course) => {
      // Page title
      this.title.setTitle(`${this.pageTitle} | ${course.courseOrerview.title}`);
    });

    this.store
      .select(selectAuthInfo)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((authInfo) => {
        this.userAuthInfo = authInfo;
      });
    this.loadUser();
  }

  getPreviewCount() {
    this.store.select(selectPreviewCalculation).subscribe((course) => {
      this.previewData = course;
    });
  }

  new_announcements_emails: boolean = false;
  promotional_emails: boolean = false;
  private getNotificationSettings(courseId: string) {
    this.courseSettingsService
      .getStudentNotificationSettings(courseId)
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        this.new_announcements_emails = settings.receive_course_educational_announcements_emails;
        this.promotional_emails = settings.receive_course_promotional_announcements_emails;
      });
  }

  updateNotificationSettings() {
    const payload = {
      product_id: this.params['courseId'],
      receive_course_promotional_announcements_emails: this.promotional_emails,
      receive_course_educational_announcements_emails: this.new_announcements_emails,
    };
    this.courseSettingsService
      .updateStudentNotificationSettings(payload)
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {});
  }

  private loadUser() {
    this.store
      .select(selectAuthInfo)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.user = { id: user.id, email: user.email, fullname: user.fullname } as User;
      });
  }

  private getCertificateInfos() {
    this.store.dispatch(CourseContentActions.loadCourseCertificate({ product_id: this.params['courseId'] }));
    this.store
      .select(selectCoursesCertificate)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((data) => {
        this.certificateDetail = data;
      });
  }

  get progress(): number {
    if (this.certificateDetail) {
      return this.certificateDetail?.progress;
    }
    return 0;
  }

  logout() {
    this.store.dispatch(AuthActions.logout());
  }

  back() {
    this.location.back();
  }

  goToNote() {
    let url = `/learning-lab/${this.params['courseId']}/notes/${this.params['section']}/${this.params['subsection']}/${this.params['overviewcode']}`;
    this.router.navigate([url]);
  }

  get fullName(): string {
    return this.userAuthInfo.fullname ?? '';
  }

  get userEmail(): string {
    return this.userAuthInfo.email ?? '';
  }

  get photoUrl(): string {
    return this.userAuthInfo.photo_url ?? '';
  }

  get instructorId(): number {
    return this.userAuthInfo.instructor_id ?? 0;
  }
}
